import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { ColumnDef } from '@tanstack/react-table';

import { api, type ResultSummaryRow } from '@/lib/api';
import { useContent, useDateTime, useLabel, useT } from '@/lib/i18n';
import { useUrlState } from '@/lib/url-state';
import { useSearchShortcut } from '@/lib/shortcuts';
import { PageHeader } from '@/components/page-header';
import { DataTable } from '@/components/data-table';
import { SavedFilters } from '@/components/saved-filters';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useModuleName } from '@/hooks/use-module-name';
import { usePersonaName } from '@/hooks/use-persona-name';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

/**
 * The results list (`05 §G1`, `plan-beta` E).
 *
 * ### What was missing
 *
 * **A result was reachable only by its id.** `GET /api/results` has existed since
 * Phase 3 and the console never listed it: you arrived at a result by finishing a
 * run, by following a link from Candidates, or by knowing the id. "Show me
 * everything scored this week" and "find the re-scored ones" had no answer, and
 * an analyst — the role that reads and cannot write — had no screen of their own.
 *
 * ### Why it lists, rather than dashboards
 *
 * Analytics already aggregates. This is the other half: the individual rows
 * behind those averages, addressable and filterable, so a number on the
 * dashboard can be taken apart. Every row links to the envelope, which is read
 * verbatim and never recomputed (`02 §1`).
 *
 * ### `supersedes` is a column, not a footnote
 *
 * Re-score writes a NEW result and retains the old one, so a candidate can have
 * several for one instance and the list would otherwise look like duplicates. The
 * column names which result a row replaced, which is the only thing that makes a
 * list of append-only results readable.
 */
const ALL = '__all__';

export function Results() {
  const t = useT();
  const moduleName = useModuleName();
  const personaName = usePersonaName();
  const label = useLabel();
  const content = useContent();
  const dateTime = useDateTime();
  const navigate = useNavigate();

  const [filters, setFilters] = useUrlState({ module: ALL, q: '', page: '1' });
  const page = Number(filters.page) || 1;
  const searchBox = useRef<HTMLInputElement>(null);
  useSearchShortcut(searchBox);

  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });

  const params = {
    page,
    pageSize: 25,
    ...(filters.module === ALL ? {} : { moduleId: filters.module }),
    ...(filters.q.trim() ? { candidateRef: filters.q.trim() } : {}),
  };
  const feed = useQuery({ queryKey: ['results', params], queryFn: () => api.results(params) });

  const columns = useMemo<ColumnDef<ResultSummaryRow, unknown>[]>(
    () => [
      {
        header: t('qcReview.column.candidate'),
        accessorKey: 'candidateRef',
        cell: ({ row }) => (
          <div>
            <p className="font-mono text-sm" dir="ltr">{row.original.candidateRef}</p>
            <p className="text-muted-foreground text-xs">{personaName(row.original.personaId)}</p>
          </div>
        ),
      },
      {
        header: t('questions.column.module'),
        accessorKey: 'moduleId',
        cell: ({ row }) => (
          <div>
            <p className="text-sm">{moduleName(row.original.moduleId)}</p>
            <p className="text-muted-foreground text-xs">
              {t('modules.configVersion', { version: row.original.configVersion })}
            </p>
          </div>
        ),
      },
      {
        header: t('results.column.flags'),
        accessorKey: 'qcFlags',
        cell: ({ row }) =>
          row.original.qcFlags.length === 0 ? (
            <span className="text-muted-foreground text-xs">{t('common.none')}</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {row.original.qcFlags.map((flag) => (
                <Badge key={flag} variant="destructive">{label('qcFlag', flag)}</Badge>
              ))}
            </div>
          ),
      },
      {
        /**
         * The lineage column. Without it a re-scored candidate reads as a
         * duplicate row, and results are append-only by design.
         */
        header: t('result.field.supersedes'),
        accessorKey: 'supersedes',
        cell: ({ row }) =>
          row.original.supersedes ? (
            <span className="font-mono text-xs" dir="ltr">{row.original.supersedes}</span>
          ) : (
            <span className="text-muted-foreground text-xs">{t('results.firstScoring')}</span>
          ),
      },
      {
        header: t('result.field.finalised'),
        accessorKey: 'finalizedAt',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">{dateTime(row.original.finalizedAt)}</span>
        ),
      },
    ],
    [t, label, dateTime, moduleName, personaName],
  );

  const filtered = filters.module !== ALL || filters.q.trim() !== '';

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title={t('nav.results')} description={t('results.description')} />

      <div className="mb-3 flex justify-end">
        <SavedFilters />
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Select value={filters.module} onValueChange={(v) => setFilters({ module: v })}>
          <SelectTrigger aria-label={t('dashboard.filterByModule')}>
            <SelectValue placeholder={t('dashboard.allModules')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('dashboard.allModules')}</SelectItem>
            {(modules.data?.items ?? []).map((m) => (
              <SelectItem key={m.moduleId} value={m.moduleId}>{content(m.name)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          ref={searchBox}
          className="sm:col-span-2"
          value={filters.q}
          onChange={(e) => setFilters({ q: e.target.value })}
          placeholder={t('results.searchPlaceholder')}
          aria-label={t('results.searchPlaceholder')}
        />
      </div>

      <DataTable
        columns={columns}
        rows={feed.data?.items ?? []}
        isLoading={feed.isLoading}
        error={feed.error}
        onRetry={() => void feed.refetch()}
        // The row IS the link — a result is one click from the list it appears
        // in, which is the whole reason the screen exists.
        onRowClick={(row) => navigate(`/results/${row.resultId}`)}
        emptyTitle={t(filtered ? 'results.noMatches' : 'results.empty.title')}
        emptyDescription={t(filtered ? 'results.noMatches.description' : 'results.empty.description')}
        emptyAction={
          filtered ? (
            <Button variant="outline" onClick={() => setFilters({ module: ALL, q: '', page: '1' })}>
              {t('questions.empty.clearFilters')}
            </Button>
          ) : undefined
        }
        page={feed.data?.page}
        pageSize={feed.data?.pageSize}
        total={feed.data?.total}
        onPageChange={(next) => setFilters({ page: String(next) })}
      />
    </div>
  );
}
